<template>
  <div>
    <!-- 面包屑 -->
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>数据分析</el-breadcrumb-item>
      <el-breadcrumb-item>进货数据</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card>
      <!-- 搜索区域 -->
      <el-row :gutter="20" style="margin-bottom: 20px; align-items: center;">
        <el-col :span="8">
          <el-input placeholder="请输入商户名称搜索" v-model="queryInfo.query">
            <el-button slot="append" icon="el-icon-search" @click="getRankList"></el-button>
          </el-input>
        </el-col>
        <el-col :span="4" style="text-align: center;">
          <el-button type="success" @click="getRankList">刷新排名</el-button>
        </el-col>
        <el-col :span="8" style="text-align: center;">
          <!-- 切换按钮 - 水平居中显示 -->
          <el-radio-group v-model="rankType" @change="handleRankTypeChange" style="display: inline-flex; align-items: center;">
            <el-radio label="num" style="margin-right: 15px;">按进货数量排名</el-radio>
            <el-radio label="price">按进货总价排名</el-radio>
          </el-radio-group>
        </el-col>
      </el-row>

      <!-- 所有进货排名柱状图 -->
      <el-card 
        shadow="hover" 
        :header="rankType === 'num' ? '进货数量排名' : '进货总价排名'" 
        style="margin-bottom: 20px;"
      >
        <div class="chart-container">
          <div ref="rankChart" class="rank-chart" style="width: 100%; height: 500px;"></div>
        </div>
      </el-card>
    </el-card>
  </div>
</template>

<script>
import $httpREQUEST from '@/utils/request';
import PUBTOOLS from '@/utils/pubtools';
import * as echarts from 'echarts';

export default {
  name: 'indata',
  data() {
    return {
      allList: [],
      queryInfo: {
        query: '',
        pagenum: 1,
        pagesize: 100,
      },
      chartInstance: null,
      defaultShowCount: 15, // 默认显示15条
      rankType: 'num', // 排名类型 num-数量 price-总价
      yAxisMax: 0, // 缓存Y轴最大值，强制固定刻度区间
      yAxisInterval: 0, // 缓存Y轴步长（固定为10的倍数）
    };
  },
  created() {
    this.getRankList();
  },
  mounted() {
    this.initChart();
    window.addEventListener('resize', this.resizeChart);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart);
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
  },
  watch: {
    allList: {
      deep: true,
      handler() {
        this.updateChart();
      },
    },
  },
  methods: {
    // 计算规整的刻度值（强制为整10的倍数）
    getRegularStepValue(value) {
      if (value <= 0) return 10; // 最小值固定为10
      
      // 计算最接近且不小于目标值的10的倍数
      const step = Math.ceil(value / 10) * 10;
      // 确保步长至少为10
      return Math.max(step, 10);
    },

    // 获取商户销售排名（适配数量/总价）
    async getRankList() {
      try {
        const apiUrl = this.rankType === 'num' 
          ? '/api/showData/numInRank' 
          : '/api/showData/priceInRank';
          
        const { data: res } = await $httpREQUEST.get(apiUrl, {
          params: PUBTOOLS.addParams(this.queryInfo),
        });

        if (res.status !== 0) {
          return this.$message.error(`获取${this.rankType === 'num' ? '数量' : '总价'}排名失败`);
        }
        this.allList = res.data.list || [];
      } catch (err) {
        this.$message.error(`获取商户${this.rankType === 'num' ? '数量' : '总价'}排名异常`);
        console.error(err);
      }
    },

    // 切换排名类型处理
    handleRankTypeChange() {
      this.yAxisMax = 0; // 重置Y轴最大值
      this.yAxisInterval = 0; // 重置步长
      this.initChart();
      this.getRankList();
    },

    // 初始化柱状图（适配数量/总价 + 固定Y轴 + 排名显示）
    initChart() {
      if (this.chartInstance) this.chartInstance.dispose();
      this.chartInstance = echarts.init(this.$refs.rankChart);

      const totalCount = this.allList.length || 1;
      const endPercent = totalCount <= this.defaultShowCount 
        ? 100 
        : (this.defaultShowCount / totalCount) * 100;

      const isPriceType = this.rankType === 'price';
      const tooltipFormatter = isPriceType 
        ? '{b}<br/>进货总价：{c} 万元' 
        : '{b}<br/>进货数量：{c} 吨';

      // 计算Y轴最大刻度（取数据最大值的1.2倍，保证刻度固定）
      const maxValue = this.allList.reduce((max, item) => {
        const value = isPriceType 
          ? Number(((item.totalInPrice || 0) / 10000).toFixed(2))
          : Number(((item.totalInNum || 0) / 1000).toFixed(2));
        return Math.max(max, value);
      }, 0);
      
      // 计算规整的Y轴最大值（强制整10）
      this.yAxisMax = maxValue > 0 ? this.getRegularStepValue(Math.ceil(maxValue * 1.2)) : 10;
      // 计算统一的步长（分割6段，强制整10）
      this.yAxisInterval = this.getRegularStepValue(Math.ceil(this.yAxisMax / 6));
      // 重新校准最大值，确保能被10的倍数步长整除
      this.yAxisMax = Math.ceil(this.yAxisMax / this.yAxisInterval) * this.yAxisInterval;

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow', shadowBlur: 0 },
          backgroundColor: 'rgba(0,0,0,0.6)',
          padding: 8,
          textStyle: { color: '#fff' },
          formatter: tooltipFormatter,
        },
        grid: {
          left: '5%', right: '5%', bottom: '18%', top: '5%', containLabel: true
        },
        dataZoom: [
          { type: 'inside', orient: 'horizontal', start: 0, end: endPercent },
          { type: 'slider', orient: 'horizontal', bottom: 5, height: 16, start: 0, end: endPercent }
        ],
        xAxis: {
          type: 'category',
          data: [],
          axisLabel: { fontSize: 12, rotate: 30, interval: 0 },
          axisLine: { lineStyle: { color: '#ccc' } },
        },
        // 强制固定Y轴刻度（整10步长）
        yAxis: {
          type: 'value',
          axisLabel: { 
            fontSize: 12,
            // 强制Y轴刻度显示为整数（10的倍数）
            formatter: function (value) {
              return Math.round(value) + (isPriceType ? ' 万元' : ' 吨');
            }
          },
          splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } },
          min: 0,          // 强制从0开始
          max: this.yAxisMax, // 强制固定最大值
          scale: false,    // 关闭自适应缩放
          interval: this.yAxisInterval, // 统一整10步长
        },
        series: [
          {
            type: 'bar',
            barWidth: '50%',
            data: [],
            // 顶部显示数值
            label: {
              show: true,
              position: 'top',
              fontSize: 12,
              fontWeight: 500,
              color: '#333',
              formatter: '{c}',
            },
            itemStyle: {
              borderRadius: [4, 4, 0, 0],
            },
          },
        ],
      };
      this.chartInstance.setOption(option);
    },

    // 更新图表数据
    updateChart() {
      if (!this.chartInstance || this.allList.length === 0) return;

      const totalCount = this.allList.length;
      const endPercent = totalCount <= this.defaultShowCount 
        ? 100 
        : (this.defaultShowCount / totalCount) * 100;

      const colorList = [
        '#ff4d4f', '#ff7a45', '#ffac38',
        '#40a9ff', '#69c0ff', '#91d5ff',
        '#36cbcb', '#5cdbd3', '#96deeb',
        '#b37feb', '#d3adf7', '#f7ba1e',
        '#ff7875', '#ff9c6e', '#ffc53d',
      ];

      // X轴数据：商户名称 + 排名（括号显示）
      const xData = this.allList.map((item, index) => `${item.factoryName}（${index + 1}）`);
      
      // 构造柱子数据
      const seriesData = this.allList.map((item, index) => ({
        name: item.factoryName,
        value: this.rankType === 'num' 
          ? Number(((item.totalInNum || 0) / 1000).toFixed(2))
          : Number(((item.totalInPrice || 0) / 10000).toFixed(2)),
        itemStyle: {
          color: colorList[index % colorList.length],
        },
      }));

      // 重新计算规整的刻度值（强制整10）
      const maxValue = seriesData.reduce((max, item) => Math.max(max, item.value), 0);
      this.yAxisMax = maxValue > 0 ? this.getRegularStepValue(Math.ceil(maxValue * 1.2)) : 10;
      this.yAxisInterval = this.getRegularStepValue(Math.ceil(this.yAxisMax / 6));
      this.yAxisMax = Math.ceil(this.yAxisMax / this.yAxisInterval) * this.yAxisInterval;

      this.chartInstance.setOption({
        dataZoom: [
          { start: 0, end: endPercent },
          { start: 0, end: endPercent }
        ],
        xAxis: { data: xData },
        yAxis: {
          max: this.yAxisMax,
          interval: this.yAxisInterval,
        },
        series: [
          { 
            data: seriesData
          }
        ],
      });
    },

    // 自适应窗口
    resizeChart() {
      if (this.chartInstance) this.chartInstance.resize();
    },
  },
};
</script>

<style scoped>
.chart-container {
  width: 100%;
  overflow-x: auto;
}
.rank-chart {
  min-width: 768px;
}

/* 滚动条美化 */
.chart-container::-webkit-scrollbar {
  height: 6px;
}
.chart-container::-webkit-scrollbar-thumb {
  background: #40a9ff;
  border-radius: 3px;
}
.chart-container::-webkit-scrollbar-track {
  background: #f7f8fa;
}

/* 单选按钮组样式优化 */
.el-radio-group {
  justify-content: center;
}

/* 确保tooltip不被遮挡 */
:deep(.echarts-custom-tooltip) {
  z-index: 9999 !important;
}
</style>